/** SessionModals — All modal dialogs for SessionDetailsModal. */
import React from 'react';
import { Loader2, Printer, AlertTriangle } from 'lucide-react';
import { useLanguage } from '../../../../i18n/LanguageContext';
import { SubSession } from '../../../../services/productionService';
import { StationWithDetails } from '../../../../services/stationManagementService';
import LabelPreviewModal from '../../../../components/LabelPreviewModal';
import AddMaterialModal from '../AddMaterialModal';
import { StationSplitEntry } from './useSessionSplitting';

interface SessionModalsProps {
  session: any;
  subSessions: SubSession[];
  availableStations: StationWithDetails[];
  stationSplitEntries: StationSplitEntry[];
  isSessionControlLoading: boolean;
  historyData: any;

  // Pause modal
  showPauseModal: boolean;
  setShowPauseModal: (v: boolean) => void;
  pauseReason: string;
  setPauseReason: (v: string) => void;
  handlePauseSession: () => void;

  // Station change modal
  showStationChangeModal: boolean;
  setShowStationChangeModal: (v: boolean) => void;
  pendingStationChange: any;
  setPendingStationChange: (v: any) => void;
  stationChangeReason: string;
  setStationChangeReason: (v: string) => void;
  stationChangeOtherReason: string;
  setStationChangeOtherReason: (v: string) => void;
  handleConfirmStationChange: () => void;
  isSavingAssignments: boolean;
  updateStationEntry: (index: number, field: string, value: string | number | null) => void;

  // Subdivide modal
  showSubdivideModal: boolean;
  setShowSubdivideModal: (v: boolean) => void;
  subdivideSubSessionId: string | null;
  setSubdivideSubSessionId: (v: string | null) => void;
  subdivideWeight: string;
  setSubdivideWeight: (v: string) => void;
  subdivideStationId: number | null;
  setSubdivideStationId: (v: number | null) => void;
  subdivideReason: string;
  setSubdivideReason: (v: string) => void;
  subdivideReasonOther: string;
  setSubdivideReasonOther: (v: string) => void;
  isSubdividing: boolean;
  handleConfirmSubdivide: () => void;
  getRemainingWeight: (ss: SubSession) => number;

  // Complete modal
  showCompleteModal: boolean;
  setShowCompleteModal: (v: boolean) => void;
  completeSubSessionId: string | null;
  setCompleteSubSessionId: (v: string | null) => void;
  completeReason: string;
  setCompleteReason: (v: string) => void;
  isCompletingSession: boolean;
  handleCompleteSubSession: () => void;

  // Cancel sub-session modal
  showCancelSubSessionConfirm: boolean;
  setShowCancelSubSessionConfirm: (v: boolean) => void;
  cancelSubSessionId: string | null;
  setCancelSubSessionId: (v: string | null) => void;
  isCancellingSubSession: boolean;
  confirmCancelSubSession: () => void;

  // Reprint modal
  reprintBoxId: string | null;
  setReprintBoxId: (id: string | null) => void;
  reprintReason: string;
  setReprintReason: (v: string) => void;
  reprintReasons: Array<{ value: string; label: string }>;
  isReprinting: boolean;
  handleReprint: (box: any) => void;

  // Label preview
  showLabelPreview: boolean;
  setShowLabelPreview: (v: boolean) => void;
  labelPreviewHtml: string;
  numberOfCopies: number;

  // Add material modal
  showAddMaterialModal: boolean;
  setShowAddMaterialModal: (v: boolean) => void;
  handleAddMaterial: (data: any) => void;
  pendingMaterials: any[];

  // Station conflict modal
  stationConflictInfo: any;
  handleCancelQueuePriority: () => void;
  handleConfirmQueuePriority: () => void;

  // Unsaved changes modal
  showUnsavedChangesModal: boolean;
  setShowUnsavedChangesModal: (v: boolean) => void;
  handleConfirmDiscard: () => void;
}

const SessionModals: React.FC<SessionModalsProps> = ({
  session,
  subSessions,
  availableStations,
  stationSplitEntries,
  isSessionControlLoading,
  historyData,

  showPauseModal,
  setShowPauseModal,
  pauseReason,
  setPauseReason,
  handlePauseSession,

  showStationChangeModal,
  setShowStationChangeModal,
  pendingStationChange,
  setPendingStationChange,
  stationChangeReason,
  setStationChangeReason,
  stationChangeOtherReason,
  setStationChangeOtherReason,
  handleConfirmStationChange,
  isSavingAssignments,
  updateStationEntry,

  showSubdivideModal,
  setShowSubdivideModal,
  subdivideSubSessionId,
  setSubdivideSubSessionId,
  subdivideWeight,
  setSubdivideWeight,
  subdivideStationId,
  setSubdivideStationId,
  subdivideReason,
  setSubdivideReason,
  subdivideReasonOther,
  setSubdivideReasonOther,
  isSubdividing,
  handleConfirmSubdivide,
  getRemainingWeight,

  showCompleteModal,
  setShowCompleteModal,
  completeSubSessionId,
  setCompleteSubSessionId,
  completeReason,
  setCompleteReason,
  isCompletingSession,
  handleCompleteSubSession,

  showCancelSubSessionConfirm,
  setShowCancelSubSessionConfirm,
  cancelSubSessionId,
  setCancelSubSessionId,
  isCancellingSubSession,
  confirmCancelSubSession,

  reprintBoxId,
  setReprintBoxId,
  reprintReason,
  setReprintReason,
  reprintReasons,
  isReprinting,
  handleReprint,

  showLabelPreview,
  setShowLabelPreview,
  labelPreviewHtml,
  numberOfCopies,

  showAddMaterialModal,
  setShowAddMaterialModal,
  handleAddMaterial,
  pendingMaterials,

  stationConflictInfo,
  handleCancelQueuePriority,
  handleConfirmQueuePriority,

  showUnsavedChangesModal,
  setShowUnsavedChangesModal,
  handleConfirmDiscard,
}) => {
  const { t } = useLanguage();

  return (
    <>
      {/* Pause Session Modal */}
      {showPauseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('sessionDetails.pauseSession')}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {t('sessionDetails.pauseSessionDesc')}
            </p>
            <select
              value={pauseReason}
              onChange={(e) => setPauseReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 mb-4"
            >
              <option value="">{t('operator.selectPauseReason')}</option>
              <option value="break_time">{t('operator.breakTime')}</option>
              <option value="equipment_issue">{t('operator.equipmentIssue')}</option>
              <option value="material_shortage">{t('operator.materialShortage')}</option>
              <option value="quality_issue">{t('operator.qualityIssue')}</option>
              <option value="shift_change">{t('operator.shiftChange')}</option>
              <option value="maintenance">{t('operator.maintenance')}</option>
              <option value="other">{t('operator.other')}</option>
            </select>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowPauseModal(false);
                  setPauseReason('');
                }}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handlePauseSession}
                disabled={!pauseReason || isSessionControlLoading}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSessionControlLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('sessionDetails.confirmPause')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Station Change Confirmation Modal (for active/paused sub-sessions) */}
      {showStationChangeModal && pendingStationChange && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('sessionDetails.reassignStation') || 'Change Station'}
            </h3>

            {/* Warning banner */}
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3 mb-4">
              <p className="text-sm text-amber-700 dark:text-amber-300">
                {t('sessionDetails.reassignStationWarning') || 'Workers will need to re-clock-in at the new station.'}
              </p>
            </div>

            {/* Reason dropdown */}
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('sessionDetails.stationChangeReason') || 'Reason for station change'} <span className="text-red-500">*</span>
            </label>
            <select
              value={stationChangeReason}
              onChange={(e) => {
                setStationChangeReason(e.target.value);
                if (e.target.value !== 'other') {
                  setStationChangeOtherReason('');
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 mb-3"
            >
              <option value="">{t('sessionDetails.selectStationChangeReason') || 'Select reason...'}</option>
              <option value="equipment_issue">{t('sessionDetails.stationChangeReasons.equipment_issue') || 'Equipment Issue'}</option>
              <option value="workload_balance">{t('sessionDetails.stationChangeReasons.workload_balance') || 'Workload Balance'}</option>
              <option value="operator_request">{t('sessionDetails.stationChangeReasons.operator_request') || 'Operator Request'}</option>
              <option value="supervisor_decision">{t('sessionDetails.stationChangeReasons.supervisor_decision') || 'Supervisor Decision'}</option>
              <option value="station_maintenance">{t('sessionDetails.stationChangeReasons.station_maintenance') || 'Station Maintenance'}</option>
              <option value="other">{t('sessionDetails.stationChangeReasons.other') || 'Other'}</option>
            </select>

            {/* Other reason text input */}
            {stationChangeReason === 'other' && (
              <input
                type="text"
                value={stationChangeOtherReason}
                onChange={(e) => setStationChangeOtherReason(e.target.value)}
                placeholder={t('sessionDetails.stationChangeOtherPlaceholder') || 'Enter reason...'}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 mb-3"
              />
            )}

            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => {
                  setShowStationChangeModal(false);
                  setStationChangeReason('');
                  setStationChangeOtherReason('');
                  setPendingStationChange(null);
                  // Revert the station selection in the entry
                  if (pendingStationChange) {
                    const subSession = subSessions.find(ss => ss.sub_session_number === pendingStationChange.subSessionNumber);
                    if (subSession) {
                      updateStationEntry(pendingStationChange.entryIndex, 'station_id', subSession.station_id);
                    }
                  }
                }}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleConfirmStationChange}
                disabled={!stationChangeReason || (stationChangeReason === 'other' && !stationChangeOtherReason.trim()) || isSavingAssignments}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSavingAssignments && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('sessionDetails.confirmStationChange') || 'Change Station'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subdivide Sub-Session Modal */}
      {showSubdivideModal && subdivideSubSessionId && (() => {
        const subdivideTarget = subSessions.find(ss => ss.sub_session_number === subdivideSubSessionId);
        if (!subdivideTarget) return null;
        const remaining = getRemainingWeight(subdivideTarget);
        const unitWeight = session.unit_weight || 1;
        // Get yield factor from sub-session's expected_yield_factor (stored as percentage, e.g., 92.20)
        // Fallback to 85% default
        const yieldFactor = subdivideTarget.expected_yield_factor || 85;
        const minWeightPerBox = yieldFactor > 0 ? unitWeight / (yieldFactor / 100) : unitWeight;
        const splitWeightNum = parseFloat(subdivideWeight) || 0;
        const originalRemaining = remaining - splitWeightNum;
        const isValidSplit = splitWeightNum > 0 && splitWeightNum < remaining &&
          originalRemaining >= minWeightPerBox && splitWeightNum >= minWeightPerBox;

        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t('sessionDetails.subdivideSession') || 'Subdivide Sub-Session'}
              </h3>

              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                {t('sessionDetails.subdivideDesc') || 'Split remaining unprocessed weight to a new sub-session.'}
              </p>

              {/* Current sub-session info */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 mb-4">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {subdivideTarget.sub_session_number}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t('sessionDetails.remainingWeight') || 'Remaining'}: {remaining.toFixed(2)} lbs
                  {' • '}
                  {t('sessionDetails.minPerSubSession') || 'Min per sub-session'}: {minWeightPerBox.toFixed(2)} lbs
                </p>
              </div>

              {/* Split weight input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('sessionDetails.splitWeight') || 'Weight to Split (lbs)'} <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.1"
                  min={minWeightPerBox}
                  max={remaining - minWeightPerBox}
                  value={subdivideWeight}
                  onChange={(e) => setSubdivideWeight(e.target.value)}
                  placeholder={`Min: ${minWeightPerBox.toFixed(2)} - Max: ${(remaining - minWeightPerBox).toFixed(2)}`}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                {splitWeightNum > 0 && (
                  <p className={`text-xs mt-1 ${isValidSplit ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
                    {isValidSplit
                      ? `Original: ${originalRemaining.toFixed(2)} lbs → New: ${splitWeightNum.toFixed(2)} lbs`
                      : t('sessionDetails.invalidSplitWeight') || 'Each sub-session must have enough weight for at least 1 box'
                    }
                  </p>
                )}
              </div>

              {/* Target station for new sub-session (optional) */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('sessionDetails.targetStation') || 'Station for New Sub-Session'} ({t('common.optional') || 'optional'})
                </label>
                <select
                  value={subdivideStationId || ''}
                  onChange={(e) => setSubdivideStationId(e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  <option value="">{t('sessionDetails.assignLater') || 'Assign later'}</option>
                  {availableStations.map((station) => (
                    <option key={station.station_id} value={station.station_id}>
                      {station.station_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Reason dropdown */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('sessionDetails.subdivideReason') || 'Reason'} <span className="text-red-500">*</span>
                </label>
                <select
                  value={subdivideReason}
                  onChange={(e) => {
                    setSubdivideReason(e.target.value);
                    if (e.target.value !== 'other') setSubdivideReasonOther('');
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  <option value="">{t('sessionDetails.selectSubdivideReason') || 'Select reason...'}</option>
                  <option value="workload_balance">{t('sessionDetails.subdivideReasons.workload_balance') || 'Workload Balance'}</option>
                  <option value="speed_up_production">{t('sessionDetails.subdivideReasons.speed_up_production') || 'Speed Up Production'}</option>
                  <option value="operator_availability">{t('sessionDetails.subdivideReasons.operator_availability') || 'Operator Availability'}</option>
                  <option value="station_availability">{t('sessionDetails.subdivideReasons.station_availability') || 'Station Availability'}</option>
                  <option value="other">{t('sessionDetails.subdivideReasons.other') || 'Other'}</option>
                </select>
                {subdivideReason === 'other' && (
                  <input
                    type="text"
                    value={subdivideReasonOther}
                    onChange={(e) => setSubdivideReasonOther(e.target.value)}
                    placeholder={t('sessionDetails.subdivideOtherPlaceholder') || 'Enter reason...'}
                    className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  />
                )}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowSubdivideModal(false);
                    setSubdivideSubSessionId(null);
                    setSubdivideWeight('');
                    setSubdivideReason('');
                    setSubdivideReasonOther('');
                    setSubdivideStationId(null);
                  }}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleConfirmSubdivide}
                  disabled={!isValidSplit || !subdivideReason || (subdivideReason === 'other' && !subdivideReasonOther.trim()) || isSubdividing}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSubdividing && <Loader2 className="w-4 h-4 animate-spin" />}
                  {t('sessionDetails.confirmSubdivide') || 'Subdivide'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Complete Session Modal (for early completion override) */}
      {showCompleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('sessionDetails.completeSession') || 'Complete Session'}
            </h3>

            {/* Warning banner */}
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3 mb-4">
              <p className="text-sm text-amber-700 dark:text-amber-300 font-medium mb-1">
                {t('sessionDetails.earlyCompletionWarning') || 'Early Session Completion'}
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-400">
                {t('sessionDetails.earlyCompletionWarningDesc') || 'This session has not reached its target. It will be marked as completed by supervisor override.'}
              </p>
            </div>

            {/* Waste tracking reminder */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3 mb-4">
              <p className="text-xs text-blue-700 dark:text-blue-300">
                {t('sessionDetails.wasteTrackingReminder') || 'Note: Ensure waste tracking has been completed at the station before closing this session.'}
              </p>
            </div>

            {/* Reason dropdown */}
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('sessionDetails.completionReason') || 'Reason for Early Completion'} <span className="text-red-500">*</span>
            </label>
            <select
              value={completeReason}
              onChange={(e) => setCompleteReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 mb-4"
            >
              <option value="">{t('sessionDetails.selectReason') || 'Select a reason...'}</option>
              <option value="shift_ended">{t('sessionDetails.reasonShiftEnded') || 'Shift Ended'}</option>
              <option value="material_shortage">{t('sessionDetails.reasonMaterialShortage') || 'Material Shortage'}</option>
              <option value="equipment_issue">{t('sessionDetails.reasonEquipmentIssue') || 'Equipment Issue'}</option>
              <option value="other">{t('sessionDetails.reasonOther') || 'Other'}</option>
            </select>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCompleteModal(false);
                  setCompleteSubSessionId(null);
                  setCompleteReason('');
                }}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {t('common.cancel') || 'Cancel'}
              </button>
              <button
                onClick={handleCompleteSubSession}
                disabled={!completeReason || isCompletingSession}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isCompletingSession && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('sessionDetails.confirmComplete') || 'Complete Session'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Session lives in the shared <CancelSessionModal>, rendered by SessionDetailsModal (WCO-2706) */}

      {/* Cancel Sub-Session Confirmation Modal */}
      {showCancelSubSessionConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {t('sessionDetails.cancelSubSession') || 'Cancel Sub-Session'}
            </h3>
            <p className="text-sm font-medium text-red-600 dark:text-red-400 mb-3">
              {subSessions.find(ss => ss.sub_session_number === cancelSubSessionId)?.sub_session_number || cancelSubSessionId}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
              {t('sessionDetails.cancelSubSessionDesc') || 'Are you sure you want to cancel this sub-session? The material weight will be returned to the lot.'}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCancelSubSessionConfirm(false);
                  setCancelSubSessionId(null);
                }}
                disabled={isCancellingSubSession}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {t('common.cancel') || 'Cancel'}
              </button>
              <button
                onClick={confirmCancelSubSession}
                disabled={isCancellingSubSession}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isCancellingSubSession && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('sessionDetails.confirmCancel') || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Label Reprint Confirmation Modal */}
      {reprintBoxId !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Printer className="w-5 h-5 text-blue-600" />
              {t('supervisor.reprintLabel')}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {t('supervisor.reprintWarning') || 'This will print a new label for this box and mark the previous label as discarded.'}
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('supervisor.reprintReason')} *
              </label>
              <select
                value={reprintReason}
                onChange={(e) => setReprintReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('supervisorOverride.selectReason')}</option>
                {reprintReasons.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setReprintBoxId(null);
                  setReprintReason('');
                }}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => {
                  const box = historyData?.boxes?.find((b: any) => b.id === reprintBoxId);
                  if (box) {
                    handleReprint({
                      id: box.id,
                      box_number: box.box_number,
                      actual_weight: box.actual_weight,
                      barcode_number: box.barcode_number,
                      packed_by: box.packed_by,
                      packed_by_name: box.packed_by_name,
                    });
                  }
                }}
                disabled={!reprintReason || isReprinting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isReprinting && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('supervisor.confirmReprint') || 'Confirm Reprint'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Label Preview Modal (TEST MODE) */}
      <LabelPreviewModal
        isOpen={showLabelPreview}
        onClose={() => setShowLabelPreview(false)}
        labelHtml={labelPreviewHtml}
        copies={numberOfCopies}
      />

      {/* Add Material Modal */}
      <AddMaterialModal
        isOpen={showAddMaterialModal}
        onClose={() => setShowAddMaterialModal(false)}
        sessionNumber={session.session_number}
        productSku={session.product_sku}
        productName={session.product_name || session.product_sku}
        productUnitWeight={session.unit_weight || 0}
        subSessions={subSessions.map((ss) => {
          const matchedMaterial = session.materials?.find((m: any) => m.sub_session_number === ss.sub_session_number)
                               || session.materials?.[0];
          return {
            sub_session_number: ss.sub_session_number,
            status: ss.status,
            initial_material_weight: Number(ss.initial_material_weight) || 0,
            item_number: matchedMaterial?.item_number || session.raw_material_item_number,
            brand: matchedMaterial?.brand
          };
        })}
        onAddMaterial={handleAddMaterial}
        pendingAllocations={pendingMaterials}
      />

      {/* Station Conflict (Priority Queue) Confirmation Modal */}
      {stationConflictInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[70] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('sessionDetails.stationConflictTitle') || 'Station Already Active'}
              </h3>
            </div>
            <div className="space-y-3 mb-6">
              <p className="text-gray-600 dark:text-gray-300">
                {(t('sessionDetails.stationConflictMessage') || 'Station {station} has an active sub-session ({subSession}).')
                  .replace('{station}', stationConflictInfo.targetStation.station_name)
                  .replace('{subSession}', stationConflictInfo.activeSubSession.sub_session_number)}
              </p>
              {stationConflictInfo.queueCount > 0 && (
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  {(t('sessionDetails.stationQueueExists') || 'There are {count} sub-sessions already queued.')
                    .replace('{count}', stationConflictInfo.queueCount.toString())}
                </p>
              )}
              <p className="text-orange-600 dark:text-orange-400 font-medium">
                {t('sessionDetails.stationPriorityQueue') || 'Your sub-session will have PRIORITY and be first in queue.'}
              </p>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                {t('sessionDetails.stationWillActivate') || 'Once the current active sub-session completes, yours will start.'}
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleCancelQueuePriority}
                disabled={isSavingAssignments}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                {t('common.cancel') || 'Cancel'}
              </button>
              <button
                onClick={handleConfirmQueuePriority}
                disabled={isSavingAssignments}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isSavingAssignments && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('sessionDetails.confirmQueuePriority') || 'Queue with Priority'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unsaved Changes Confirmation Modal */}
      {showUnsavedChangesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[70] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('sessionDetails.unsavedChanges') || 'Unsaved Changes'}
              </h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              {t('sessionDetails.unsavedChangesMessage') || 'You have unsaved changes. Are you sure you want to close without saving?'}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowUnsavedChangesModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                {t('common.cancel') || 'Cancel'}
              </button>
              <button
                onClick={handleConfirmDiscard}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                {t('sessionDetails.discardChanges') || 'Discard Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SessionModals;
